import {styled, keyframes} from 'styled-components';
import { StatusBadge } from './CommonStyles';

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing(3)};
  padding: ${props => props.theme.spacing(2)};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing(2)};
  }
`;

export const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.theme.colors.primary};
    opacity: 0.7;
  }

  ${props => props.isLoading && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${props.theme.colors.surface};
      animation: pulse 1.5s infinite;
    }
  `}
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(2)};
`;

export const LeadName = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: ${props => props.theme.typography.fontSize.medium};
  margin: 0;
`;

export const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(2)};
  color: ${props => props.theme.colors.textPrimary};
  
  svg {
    color: ${props => props.theme.colors.primary};
    font-size: 1.2rem;
  }
`;

export const ActionIconsContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing(2)};
  padding-top: ${props => props.theme.spacing(2)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}33;
`;

export const DescriptionContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  justify-content: middle;
  margin-top: ${props => props.theme.spacing(2)};
  padding-top: ${props => props.theme.spacing(2)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}33;
`;

export const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  text-decoration: none;
  padding: ${props => props.theme.spacing(1)};
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.primary}11;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const EmptyState = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${props => props.theme.spacing(6)};
  text-align: center;
  color: ${props => props.theme.colors.textSecondary};
  gap: ${props => props.theme.spacing(2)};

  svg {
    opacity: 0.5;
  }
`;

const pulseAnimation = keyframes`
  0% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    opacity: 0.6;
  }
`;

export const FilterSection = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(3)};
  flex-wrap: wrap;
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.borderRadius};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing(1)};
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const FilterButton = styled.button`
  background: ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${props => props.isActive ? props.theme.colors.black : props.theme.colors.textPrimary};
  border: 1px solid ${props => props.isActive ? props.theme.colors.primary : props.theme.colors.inputGray};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};

  &:hover {
    background: ${props => props.theme.colors.primary}22;
  }

  svg {
    font-size: 1rem;
  }
`;

export const FilterLabel = styled.span`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
`;

export const OriginBadge = styled(StatusBadge)`
  position: absolute;
  top: ${props => props.theme.spacing(2)};
  right: ${props => props.theme.spacing(2)};
  font-size: ${props => props.theme.typography.fontSize.small};
`;

export const OriginCard = styled(Card)`
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  ${CardHeader} {
    border-bottom: 1px solid ${props => props.theme.colors.inputGray}33;
  }

  ${LeadName} {
    display: flex;
    align-items: center;
    font-size: ${props => props.theme.typography.fontSize.medium};
    
    svg {
      color: ${props => props.theme.colors.primary};
    }
  }

  ${CardContent} {
    flex-grow: 1;
    padding: ${props => props.theme.spacing(2)} 0;
  }

  ${InfoRow} {
    font-size: ${props => props.theme.typography.fontSize.small};
    color: ${props => props.theme.colors.textSecondary};
    
    svg {
      font-size: 1rem;
    }
  }

  ${ActionIconsContainer} {
    margin-top: auto;
    padding-top: ${props => props.theme.spacing(2)};
  }
`;

export const LoadingCard = styled(Card)`
  pointer-events: none;

  ${CardHeader}, ${CardContent} {
    div {
      background: linear-gradient(90deg,
        ${props => props.theme.colors.inputGray}33 0%,
        ${props => props.theme.colors.inputGray}66 50%,
        ${props => props.theme.colors.inputGray}33 100%
      );
      background-size: 1000px 100%;
      animation: ${shimmer} 2s infinite linear;
      border-radius: ${props => props.theme.borderRadius};
    }
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.theme.colors.surface}11;
  }
`;